import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import './pages/form';
import './pages/results';
import './pages/details';
import './pages/compare';
import './pages/applicant-information';
import './pages/producer-email-page';
import { choose } from 'lit/directives/choose.js';
import { provide } from '@lit/context';
import { breakpointContext } from './contexts/breakpoint';
import { BREAKPOINT, getBreakpoint } from './util/breakpoints';
import { EVENT, makeCustomEvent } from './util/events';
import { getIshopForm, ISHOP_FORM_ID, RateQuoteFormData, transformResponseToFormData } from './util/form';
import { inputResponseContext } from './contexts/inputResponse';
import { BackendFormResponse, Producer } from './util/api';
import { SOURCE, sourceMap } from './util/source';
import { sourceContext } from './contexts/source';
import { deliveryMethodContext } from './contexts/deliveryMethod';
import './components/agent-banner';
import { agentContext } from './contexts/agent';
import { PAGE } from './util/page';
import './components/hidden-form';
import { getEmailPlans } from './controllers/emailData';
import './components/footer';
import './components/exit-modal';
import { STORAGE_KEYS } from './util/storage';
import { when } from 'lit/directives/when.js';
import { readOnlyContext } from './contexts/readOnly';

const bcbsncUrl = import.meta.env.VITE_BCBSNC_URL || 'https://example.com';

const MEMBER_URL = `${import.meta.env.VITE_MEMBER_URL}/blueconnect/web/login`;
const BUYONLINE_DASH_URL = `${bcbsncUrl}/sapps/buyonline/application/OnlineApplication.action`;

const INITIAL_PAGE = PAGE.FORM;

@customElement('rate-quote-app')
export class RateQuoteContainer extends LitElement {
    static styles = css`
        :host {
            margin: 0;
            width: 100%;
            height: 100vh;
            display: flex;
            flex-direction: column;
        }
        .title-button {
            width: 100%;
            display: flex;
            justify-content: end;
        }
        .header-button {
            width: 100%;
            display: flex;
            justify-content: start;
        }
        .header-actions {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: flex-end;
            flex-wrap: nowrap;
            gap: 8px;
            max-width: 100%;
            min-width: 0;
        }
        .header-actions lh-button {
            white-space: nowrap;
            flex-shrink: 0;
        }
        .footer {
            margin-top: 0;
            padding-top: 20px;
        }
        #top {
            position: absolute;
            top: 0;
            z-index: -10;
            width: 100%;
        }
        a.text-color {
            color: var(--lh-color-neutral-white, #fff);
        }
        .lh-footer__disclosures,
        .lh-footer__fine-print {
            * {
                font-family: var(--lh-font-family-sans-serif);
                font-size: var(--lh-text-font-size-xs);
                color: var(--lh-theme-color-content-default, #222425);
                margin-bottom: 0;
                margin-top: 0;
            }

            a {
                color: var(--mode-typography-text);
            }

            p + *,
            ul + * {
                margin-block-start: var(--lh-spacing-16);
            }
        }
    `;

    @provide({ context: breakpointContext })
    @state()
    breakpoint: BREAKPOINT = BREAKPOINT.LARGE_SCREEN;

    @provide({ context: inputResponseContext })
    @state()
    inputResponse!: BackendFormResponse;

    @property({ attribute: false })
    data?: BackendFormResponse;

    @property()
    page?: PAGE;

    @property({ attribute: false })
    planIds?: string[];

    @property({ attribute: 'plan-id' })
    planId?: string;

    @query('#top')
    topDiv!: HTMLElement;

    @provide({ context: sourceContext })
    @property()
    source: SOURCE = SOURCE.DIRECT_SHOPPER;

    // Delivery method chosen on the applicant-information form (e.g. 'pabol' | 'email').
    // Used to make SOURCE.EMBEDDED_AGENT act like SOURCE.EMAIL when 'email' is chosen.
    @provide({ context: deliveryMethodContext })
    @property()
    deliveryMethod: string = '';

    @provide({ context: agentContext })
    @property({ attribute: false })
    agent?: Producer;

    @property()
    quoteId?: string;

    @property()
    userId?: string;

    @provide({ context: readOnlyContext })
    @property({ type: Boolean, reflect: true })
    readOnly = false;

    private submittedForm!: RateQuoteFormData;

    @state()
    initSubsidyStep = false;

    @state()
    private loading = false;

    @state()
    private isTopPick: string | boolean = false;

    get renderPage() {
        return choose(this.page, [
            [
                PAGE.FORM,
                () => {
                    // Remove any previous filters from session storage when going back to form
                    this.clearSessionFilters();
                    return html`<rate-quote-form
                        .initSubsidyStep=${this.initSubsidyStep}
                        .submittedForm=${this.submittedForm}
                    ></rate-quote-form>`;
                },
            ],
            [
                PAGE.RESULTS,
                () =>
                    html`<rq-marketplace-results
                        .submittedForm=${this.submittedForm}
                        .isTopPick=${this.isTopPick}
                    ></rq-marketplace-results>`,
            ],
            [
                PAGE.DETAILS,
                () =>
                    html`<rq-marketplace-details
                        .plan=${this.inputResponse?.plans.find(({ planId }) => planId === this.planIds?.[0])}
                    ></rq-marketplace-details>`,
            ],
            [
                PAGE.COMPARE,
                () =>
                    html`<rq-marketplace-compare
                        .plans=${this.inputResponse?.plans.filter(({ planId }) => this.planIds?.includes(planId))}
                    ></rq-marketplace-compare>`,
            ],
            [PAGE.APPLICANT_INFORMATION, () => html`<rq-applicant-information></rq-applicant-information>`],
            [
                PAGE.PRODUCER_EMAIL_ENTRY,
                () =>
                    html`<lh-container
                        context-id="medicare-rate-quote"
                        theme="neutral-light"
                        padding-bottom="xl"
                        gutters=${this.isMobile ? 'md' : 'xl'}
                        content-width="none"
                        padding-top="lg"
                        spacing="none"
                        ><rq-producer-email-entry></rq-producer-email-entry
                    ></lh-container>`,
            ],
        ]);
    }
    get renderDisclosure() {
        return html`
            <p>Disclosures:</p>
            ${
                this.page === PAGE.DETAILS || this.page === PAGE.COMPARE
                    ? html`
                          <p>
                              <sup>1</sup> Out-of-network Emergency Services are covered at the in-network benefit level
                              for all plans.
                          </p>
                      `
                    : nothing
            }
            <p>
                <strong>Attention:</strong> This website is operated by Blue Cross and Blue Shield of North Carolina and
                is not the Health Insurance Marketplace® website at
                <a class="text-color" href="https://HealthCare.gov" target="_blank">HealthCare.gov</a>. This website
                does not display all Qualified Health Plans (QHPs) available through
                <a class="text-color" href="https://HealthCare.gov" target="_blank">HealthCare.gov</a>. To see all
                available QHP options, go to the Health Insurance Marketplace® website at
                <a class="text-color" href="https://HealthCare.gov" target="_blank">HealthCare.gov</a>.
            </p>
            <p>
                Also, you should visit the Health Insurance Marketplace® website at
                <a class="text-color" href="https://HealthCare.gov" target="_blank">HealthCare.gov</a> if:
            </p>
            <ol>
                <li>You want to select a catastrophic health plan.</li>
                <li>You want to enroll members of your household in dental coverage.</li>
            </ol>
            <p>Actual subsidy is determined by the federal government.</p>
            $0 benefits: Benefits and member cost share vary by plan design.</p>
            <p>
                Special Enrollment Periods (SEPs) are available for individuals with qualifying life events.
                The federal government determines subsidy eligibility and amount for health insurance plans
                purchased on the federally facilitated Health Insurance Marketplace. Details regarding SEP
                requirements and subsidies can be found at HealthCare.gov.
            </p>
            <p>
                Health Insurance Marketplace is a registered trademark of the Department of Health & Human
                Services.
            </p>
        `;
    }

    constructor() {
        super();
        this.popStateListener = this.popStateListener.bind(this);
        this.recommendPlanListener = this.recommendPlanListener.bind(this);
    }

    connectedCallback(): void {
        super.connectedCallback();
        // Extract the hash fragment and query parameters
        const hash = window.location.hash; // e.g., "#/viewQuote?quoteId=F1A380F621EF11F08A53096DB4C1EFF6&topPick=false"
        const queryString = hash.includes('?') ? hash.split('?')[1] : window.location.search;
        const urlParams = new URLSearchParams(queryString);

        // Handle productTypeName redirect from legacy system
        const productTypeName = urlParams.get('productTypeName');
        const currentPath = window.location.pathname;
        const alreadyOnApplicantInfo = currentPath.includes('applicant-information');
        if (productTypeName === 'BlueAdvantage' && !alreadyOnApplicantInfo) {
            // Redirect to applicant information page with clean URL
            const baseUrl = window.location.origin + (import.meta.env.BASE_URL || '/get-a-quote/');
            const userID = urlParams.get('userID');
            const queryParams = userID
                ? `?psale=y&productTypeName=BlueAdvantage&userID=${userID}`
                : '?psale=y&productTypeName=BlueAdvantage';
            window.location.href = baseUrl + 'applicant-information' + queryParams;
            return;
        }

        const quoteId = urlParams.get('quoteId');
        this.userId = urlParams.get('userId') ?? undefined;
        if (urlParams.get('psale') === 'y') {
            this.source = SOURCE.PABOL;
        }
        // TODO: fix me for top pick
        // this.isTopPick = urlParams.get('isTopPick') ?? true;
        // If readOnly is set by the consumer as true, don't overwrite it
        if (!this.readOnly) {
            this.readOnly = queryString.indexOf('readOnly') > -1;
        }
        const qId = quoteId || this.quoteId;
        if (qId) {
            if (this.quoteId !== qId) {
                this.quoteId = qId;
            }
        }

        this.addEventListener(EVENT.CHANGE_PAGE, this.handlePageChange as EventListener);
        this.addEventListener(EVENT.UPDATE_RESPONSE, this.updateInputResponse as EventListener);
        window.addEventListener(EVENT.RECOMMEND_PLAN, this.recommendPlanListener as EventListener);
        this.addEventListener(EVENT.EDIT_INFO, this.editInfoListener as EventListener);
        this.addEventListener(EVENT.SCROLL_TO_TOP, this.scrollToTop);
        addEventListener('resize', () => this.handleResize());
        window.addEventListener('popstate', this.popStateListener);
        this.handleResize();
        window.addEventListener('beforeunload', this.clearSessionFilters);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        removeEventListener('resize', () => this.handleResize());
        this.removeEventListener(EVENT.CHANGE_PAGE, this.handlePageChange as EventListener);
        this.removeEventListener(EVENT.UPDATE_RESPONSE, this.updateInputResponse as EventListener);
        window.removeEventListener(EVENT.RECOMMEND_PLAN, this.recommendPlanListener as EventListener);
        this.removeEventListener(EVENT.EDIT_INFO, this.editInfoListener as EventListener);
        this.removeEventListener(EVENT.SCROLL_TO_TOP, this.scrollToTop);
        window.removeEventListener('popstate', this.popStateListener);
        window.removeEventListener('beforeunload', this.clearSessionFilters);
    }

    private clearSessionFilters() {
        sessionStorage.removeItem(STORAGE_KEYS.FILTERS);
    }

    protected async firstUpdated(): Promise<void> {
        if (this.quoteId) {
            try {
                this.loading = true;
                const emailData = await getEmailPlans(this.quoteId);
                // TODO: fix below with top pick including undoing pickOrder deletion
                this.inputResponse = this.removePickOrder(emailData);
                if (this.inputResponse?.producer) this.agent = this.inputResponse?.producer;
            } catch (err) {
                console.error('Error fetching email data: ', err);
            } finally {
                this.loading = false;
            }
        }
        if (this.data && this.data?.plans) {
            // TODO: fix below with top pick including undoing pickOrder deletion
            this.inputResponse = this.removePickOrder(this.data);
        }

        // Set agent from response when context is PRODUCER and referralSource is producer/quoted-sale
        if (this.data) {
            this.setAgentFromResponse(this.data);
        }

        const sourceFromData = sourceMap.get(this.data?.referralSource as string);
        if (!Object.values(SOURCE).includes(this.source)) {
            this.source = sourceFromData || SOURCE.DIRECT_SHOPPER;
        }
        if (typeof this.planIds === 'string') {
            this.planIds = [this.planIds as string];
        }
        if (typeof this.planId === 'string') {
            this.planIds = [this.planId as string];
        }
        // Extract page from pathname, handling /get-a-quote/ base path
        const pathname = new URL(window.location.href).pathname;
        const pageFromUrl = pathname
            .replace(/^\/get-a-quote\//, '')
            .replace(/^\/$/, '')
            .toLowerCase();

        const page = this.page;
        if (this.quoteId) {
            this.page = PAGE.RESULTS;
        } else if (pageFromUrl === 'plans') {
            // Map 'plans' URL to RESULTS page for Blue Advantage flow
            this.page = PAGE.RESULTS;
            // Ensure query params are preserved
            if (!window.location.search.includes('psale=y')) {
                const baseUrl = window.location.origin + (import.meta.env.BASE_URL || '/get-a-quote/');
                const urlParams = new URLSearchParams(window.location.search);
                const userID = urlParams.get('userID');
                const queryParams = userID
                    ? `?psale=y&productTypeName=BlueAdvantage&userID=${userID}`
                    : '?psale=y&productTypeName=BlueAdvantage';
                window.history.replaceState({}, '', baseUrl + 'plans' + queryParams);
            }
        } else if (pageFromUrl && Object.values(PAGE).includes(pageFromUrl as PAGE)) {
            this.page = pageFromUrl as PAGE;
            // Ensure ?psale=y is always present in URL for applicant-information
            if (pageFromUrl === PAGE.APPLICANT_INFORMATION && !window.location.search.includes('psale=y')) {
                const baseUrl = window.location.origin + (import.meta.env.BASE_URL || '/get-a-quote/');
                const urlParams = new URLSearchParams(window.location.search);
                const userID = urlParams.get('userID');
                const queryParams = userID
                    ? `?psale=y&productTypeName=BlueAdvantage&userID=${userID}`
                    : '?psale=y&productTypeName=BlueAdvantage';
                window.history.replaceState({}, '', baseUrl + 'applicant-information' + queryParams);
            }
        } else if (page && Object.values(PAGE).includes(page.toLowerCase() as PAGE)) {
            this.page = page.toLowerCase() as PAGE;
        } else {
            this.page = INITIAL_PAGE;
        }
        if (!this.agent && this.data?.producer) {
            this.agent = this.data?.producer;
        }
        // window.history.pushState({}, '', `/${this.page}`);
    }

    private popStateListener(event: PopStateEvent) {
        const { pathname, hash } = (event.target as Window).location;
        // Extract page from pathname, handling /get-a-quote/ base path
        const page = pathname.replace(/^\/get-a-quote\//, '').replace(/^\/$/, '');

        if (hash.includes('step-')) {
            if (this.page !== PAGE.FORM) {
                this.page = PAGE.FORM;
            }
            const hashNumber = Number(hash.replace('#step-', ''));
            this.dispatchEvent(makeCustomEvent(EVENT.MANUAL_STEP, { step: hashNumber }));
            this.scrollToTop();
            return;
        }

        if (Object.values(PAGE).includes(page as PAGE)) {
            this.page = page as PAGE;
        } else {
            this.page = INITIAL_PAGE;
        }
        this.scrollToTop();
    }

    private editInfoListener(e: CustomEvent) {
        if (this.source === SOURCE.PABOL || (this.source === SOURCE.EMAIL && this.agent)) {
            const baseUrl = window.location.origin + (import.meta.env.BASE_URL || '/get-a-quote/');
            const urlParams = new URLSearchParams(window.location.search);
            const userID = urlParams.get('userID');
            const queryParams = userID
                ? `?psale=y&productTypeName=BlueAdvantage&userID=${userID}`
                : '?psale=y&productTypeName=BlueAdvantage';
            window.history.pushState({}, '', baseUrl + 'applicant-information' + queryParams);
            this.page = PAGE.APPLICANT_INFORMATION;
            this.scrollToTop();
            return;
        }
        if (this.inputResponse) {
            this.submittedForm = transformResponseToFormData(
                this.inputResponse,
                this.submittedForm?.severity,
                this.submittedForm
            );
        }
        this.initSubsidyStep = Boolean(e?.detail?.subsidy);
        this.page = PAGE.FORM;
        // window.history.pushState({}, '', `/${this.page}`);
        this.scrollToTop();
    }

    private recommendPlanListener(e: CustomEvent) {
        const planId = e.detail.planId;
        const plans = this.inputResponse?.plans;
        if (!plans || plans.length === 0 || !planId) return;
        plans.forEach((plan) => {
            plan.producerRecommended = plan.planId === planId;
        });
        const updatedResponse = { ...this.inputResponse, plans };
        // TODO: fix below with top pick including undoing pickOrder deletion
        this.inputResponse = this.removePickOrder(updatedResponse);
        if (!this.inputResponse.renewal) {
            const blueAIshopForm = getIshopForm(ISHOP_FORM_ID.BLUE_ADVANTAGE);
            if (blueAIshopForm) {
                blueAIshopForm.selection.value = planId;
            } else {
                console.error('Cannot find document form: ', ISHOP_FORM_ID.BLUE_ADVANTAGE);
            }
        }
    }

    private handleResize() {
        const breakpoint = getBreakpoint(window.outerWidth);
        if (this.breakpoint === breakpoint) return;
        this.breakpoint = breakpoint;
    }

    private handlePageChange(e: CustomEvent) {
        if (!e.detail.page) return;
        const planId = e.detail?.planId;
        const planIds: string[] = e.detail?.planIds;
        if (planIds?.length && typeof planIds === 'object') {
            this.planIds = planIds;
        } else if (typeof planIds === 'string') {
            this.planIds = [planIds];
        } else if (planId && typeof planId === 'string') {
            this.planIds = [planId];
        }
        if (e.detail.page === PAGE.APPLICANT_INFORMATION) {
            const baseUrl = window.location.origin + (import.meta.env.BASE_URL || '/get-a-quote/');
            const urlParams = new URLSearchParams(window.location.search);
            const userID = urlParams.get('userID');
            const queryParams = userID
                ? `?psale=y&productTypeName=BlueAdvantage&userID=${userID}`
                : '?psale=y&productTypeName=BlueAdvantage';
            window.history.pushState({}, '', baseUrl + 'applicant-information' + queryParams);
        }
        if (e.detail.page === PAGE.RESULTS) {
            const baseUrl = window.location.origin + (import.meta.env.BASE_URL || '/get-a-quote/');
            const urlParams = new URLSearchParams(window.location.search);
            const userID = urlParams.get('userID');
            const queryParams = userID
                ? `?psale=y&productTypeName=BlueAdvantage&userID=${userID}`
                : '?psale=y&productTypeName=BlueAdvantage';
            window.history.pushState({}, '', baseUrl + 'plans' + queryParams);
        }
        this.page = e.detail.page;
        this.scrollToTop();
    }

    private setAgentFromResponse(data: BackendFormResponse) {
        const responseContext = data?.context?.toUpperCase() ?? '';
        const referralSource = data?.referralSource?.toUpperCase() ?? '';
        const isProducerResponse =
            responseContext === 'PRODUCER' &&
            (referralSource === 'PRODUCER_ENTERED' || referralSource === 'QUOTED_SALE');
        if (isProducerResponse && data?.producer) {
            this.agent = data.producer;
            if (referralSource === 'PRODUCER_ENTERED') {
                this.source = SOURCE.PABOL;
            } else if (referralSource === 'QUOTED_SALE') {
                // Don't downgrade a source already established as PABOL (e.g. via psale=y).
                const path = window.location.pathname;
                const isRenewalChangeAcaPlan = path === '/sapps/ratequote/producer/RenewalChangeAcaPlan.do';

                // Renewal Change ACA Plan should keep agent behavior
                this.source = isRenewalChangeAcaPlan ? SOURCE.AGENT : SOURCE.EMAIL;
            }
        }
    }

    private updateInputResponse(e: CustomEvent) {
        if (!e.detail.data) return;
        if (e.detail.formData) {
            this.submittedForm = e.detail.formData as RateQuoteFormData;
        }
        if (typeof e.detail.deliveryMethod === 'string') {
            this.deliveryMethod = e.detail.deliveryMethod;
        }
        // TODO: fix below with top pick including undoing pickOrder deletion
        // this.inputResponse = { ...e.detail.data, plansSeverity: e.detail?.severity };
        const data = this.removePickOrder(e.detail.data as BackendFormResponse);
        this.inputResponse = { ...data };
        if (data.quoteId) {
            this.quoteId = data.quoteId;
        }
        this.setAgentFromResponse(this.inputResponse);
        // window.location.hash = '';
        // window.history.pushState({}, '', `/${PAGE.RESULTS}`);
        this.page = PAGE.RESULTS;
        this.scrollToTop();
    }

    // TODO: remove below with top pick including undoing pickOrder deletion
    private removePickOrder(data: BackendFormResponse): BackendFormResponse {
        const updateData = { ...data };
        (updateData?.plans || []).forEach((plan) => {
            delete plan.pickOrder;
        });
        return updateData;
    }

    private scrollToTop() {
        if (!this.topDiv) return;
        this.topDiv.scrollIntoView({ behavior: 'auto', block: 'end' });
    }

    get memberLoginButton() {
        if (
            this.breakpoint === BREAKPOINT.PHONE ||
            this.breakpoint === BREAKPOINT.TABLET ||
            (this.agent && this.data?.context.toLocaleLowerCase() !== 'shopper') ||
            this.source === SOURCE.CHANGE_APP ||
            this.source === SOURCE.PABOL
        )
            return nothing;
        return html`<lh-button
            href=${MEMBER_URL}
            variant="outlined"
            icon-position="left"
            icon="user"
            size="md"
            analytics-id="rate-quote:${this.source}:u65:login:button-filled"
            >Member Login</lh-button
        >`;
    }

    private get shouldRenderExitModal(): boolean {
        if (typeof window !== 'undefined') {
            const isBlueAdvantagePlansPage =
                window.location.pathname.includes('/get-a-quote/plans') &&
                new URLSearchParams(window.location.search).get('productTypeName') === 'BlueAdvantage';

            if (isBlueAdvantagePlansPage) {
                return false;
            }
        }

        return this.source === SOURCE.DIRECT_SHOPPER || this.source === SOURCE.EMBEDDED_QUOTE;
    }
    get isMobile() {
        return this.breakpoint === BREAKPOINT.PHONE || this.breakpoint === BREAKPOINT.TABLET;
    }

    render() {
        const footerPage:
            | PAGE.FORM
            | PAGE.RESULTS
            | PAGE.DETAILS
            | PAGE.COMPARE
            | PAGE.APPLICANT_INFORMATION
            | undefined = this.page === PAGE.PRODUCER_EMAIL_ENTRY ? PAGE.RESULTS : this.page;

        return html` ${this.loading
            ? html`<lh-loader></lh-loader>`
            : html`${when(
                      this.source !== SOURCE.EMBEDDED_AGENT,
                      () =>
                          html`<lh-header sticky>
                              <lh-logo slot="logo" name="bcbsnc-short"></lh-logo>
                              ${this.page === PAGE.APPLICANT_INFORMATION
                                  ? html`
                                        <lh-typography
                                            slot="title"
                                            class="agent-header-title"
                                            type="text"
                                            size="lg"
                                            weight="700"
                                        >
                                            Rate Quote for Individual and Family Plans
                                        </lh-typography>
                                    `
                                  : nothing}
                              <div slot="action" class="header-actions">
                                  ${this.memberLoginButton}
                                  ${(this.agent && this.data?.context.toLocaleLowerCase() !== 'shopper') ||
                                  this.source === SOURCE.CHANGE_APP ||
                                  this.source === SOURCE.PABOL
                                      ? nothing
                                      : html`<lh-button
                                            href=${BUYONLINE_DASH_URL}
                                            variant="filled"
                                            size="md"
                                            icon-position="left"
                                            icon=${this.breakpoint !== BREAKPOINT.PHONE ? 'clipboard-text' : nothing}
                                            analytics-id="rate-quote:${this.source}:u65:resume-application:button-text"
                                            >Resume an Application</lh-button
                                        >`}
                              </div>
                          </lh-header>`
                  )}
                  <span id="top"></span>
                  <div
                      style=${this.page === PAGE.APPLICANT_INFORMATION
                          ? 'background-color: var(--lh-color-neutrallight, #f8f5f1);'
                          : ''}
                  >
                      <rq-hidden-form .quoteId=${this.quoteId} .userId=${this.userId} .agentInfo=${this.agent}>
                          ${this.renderPage}
                      </rq-hidden-form>
                  </div>
                  ${this.page === PAGE.PRODUCER_EMAIL_ENTRY
                      ? nothing
                      : html`<footer-component .page=${footerPage}></footer-component>`} `}
        ${this.shouldRenderExitModal ? html`<rq-exit-modal></rq-exit-modal>` : nothing}`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'rate-quote-app': RateQuoteContainer;
    }
    interface Window {
        bcncDataLayer: {
            page?: {
                view_url?: string;
            };
        };
    }
}
